import React, { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom';
import { Button } from '../../ui/button';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1.25;

type EnlargedMediaZoomProps = {
  children: ReactElement;
  /** Change this when the dialog opens or closes so the transform resets. */
  resetToken?: string | number | boolean;
};

type PinchZoomHandle = {
  scaleTo: (options: { x: number; y: number; scale: number; animated?: boolean }) => void;
};

export function EnlargedMediaZoom({ children, resetToken }: EnlargedMediaZoomProps) {
  const zoomRef = useRef<PinchZoomHandle | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const scaleRef = useRef(MIN_ZOOM);
  const [scale, setScale] = useState(MIN_ZOOM);

  const applyTransform = useCallback((x: number, y: number, nextScale: number) => {
    const node = contentRef.current;
    if (!node) return;
    node.style.setProperty('transform', make3dTransformValue({ x, y, scale: nextScale }));
    scaleRef.current = nextScale;
    setScale(nextScale);
  }, []);

  const onUpdate = useCallback(
    ({ x, y, scale: nextScale }: { x: number; y: number; scale: number }) => {
      applyTransform(x, y, nextScale);
    },
    [applyTransform]
  );

  const zoomTo = useCallback((nextScale: number) => {
    const node = contentRef.current;
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextScale));
    const x = node ? node.offsetWidth / 2 : 0;
    const y = node ? node.offsetHeight / 2 : 0;
    zoomRef.current?.scaleTo({ x, y, scale: clamped, animated: true });
  }, []);

  const resetZoom = useCallback(() => {
    zoomRef.current?.scaleTo({ x: 0, y: 0, scale: MIN_ZOOM, animated: true });
    applyTransform(0, 0, MIN_ZOOM);
  }, [applyTransform]);

  useEffect(() => {
    resetZoom();
  }, [resetToken, resetZoom]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomTo(scaleRef.current * ZOOM_STEP);
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        zoomTo(scaleRef.current / ZOOM_STEP);
      } else if (event.key === '0') {
        event.preventDefault();
        resetZoom();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [resetZoom, zoomTo]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 pr-8">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1 text-xs"
          onClick={() => zoomTo(scaleRef.current * ZOOM_STEP)}
          aria-label="Zoom in"
        >
          <Plus className="h-3.5 w-3.5" />
          Zoom in
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1 text-xs"
          onClick={() => zoomTo(scaleRef.current / ZOOM_STEP)}
          aria-label="Zoom out"
        >
          <Minus className="h-3.5 w-3.5" />
          Zoom out
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1 text-xs"
          onClick={resetZoom}
          aria-label="Reset zoom"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </Button>
        <span className="text-xs text-gray-500">Scroll to pan; pinch or use +/− to zoom</span>
        <span className="text-xs tabular-nums text-gray-400">{Math.round(scale * 100)}%</span>
      </div>
      <div className="min-h-[50vh] max-h-[calc(90vh-10rem)] flex-1 overflow-auto overscroll-contain rounded-md border border-gray-100 bg-gray-50/40">
        <QuickPinchZoom
          ref={zoomRef as never}
          onUpdate={onUpdate}
          shouldInterceptWheel={() => false}
          draggableUnZoomed={false}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          containerProps={{ className: 'min-h-full min-w-full' }}
        >
          <div ref={contentRef} className="inline-block min-w-full origin-top-left p-2">
            {children}
          </div>
        </QuickPinchZoom>
      </div>
    </div>
  );
}
